import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { fmtFecha } from '../../../lib/utils';
import { SearchableCombobox } from '../../../components/ui/searchable-combobox';
import { Label } from '../../../components/ui/label';
import { fmtCLP } from './egresosUtils';

// Iter 390 — Cuenta contable del egreso (plan de cuentas Alegra) + documentos pendientes cuando la cuenta es CxP proveedores.
export const MODO_LABEL = {
  documentos: 'Paga documentos pendientes del proveedor (monto = Σ saldos, pago completo)',
  anticipo: 'Anticipo a proveedor: queda pendiente de aplicar a factura',
  transferencia: 'Transferencia entre cuentas propias: egreso en origen + ingreso en destino (contracuenta "Transferencias bancarias")',
  gasto: 'Gasto / pago directo a la cuenta contable (sin documentos)',
};

let cacheCuentas = null;

export function CuentaContableSelect({ value, onChange }) {
  const [cuentas, setCuentas] = useState(cacheCuentas || []);

  useEffect(() => {
    if (cacheCuentas) return;
    api.get('/contabilidad/egresos/cuentas-contables').then((r) => {
      cacheCuentas = r.data.cuentas || [];
      setCuentas(cacheCuentas);
      if (r.data.error) toast.error(`Plan de cuentas: ${r.data.error}`);
    }).catch(() => toast.error('No se pudo cargar el plan de cuentas'));
  }, []);

  useEffect(() => {
    if (!value && cuentas.length) {
      const def = cuentas.find((c) => c.modo === 'anticipo') || cuentas[0];
      onChange(def);
    }
  }, [cuentas, value, onChange]);

  const items = useMemo(() => cuentas.map((c) => ({ value: c.id, label: c.name, subLabel: [c.code, c.grupo].filter(Boolean).join(' · '), cuenta: c })), [cuentas]);

  return (
    <div>
      <Label className="text-xs">Cuenta contable *</Label>
      <SearchableCombobox items={items} value={value?.id || ''} placeholder="Buscar cuenta…" testid="select-cuenta-contable"
        onChange={(item) => onChange(item ? item.cuenta : null)} />
      {value && <p className="text-[10px] text-muted-foreground mt-0.5" data-testid="cuenta-modo-hint">{MODO_LABEL[value.modo]}</p>}
    </div>
  );
}

export function DocumentosPendientes({ rut, seleccion, onSeleccion }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!rut) { setDocs([]); return; }
    setLoading(true);
    api.get('/contabilidad/egresos/documentos-pendientes', { params: { rut } })
      .then((r) => setDocs(r.data.documentos || []))
      .catch((e) => { setDocs([]); toast.error(e?.response?.data?.detail || 'No se pudieron cargar los documentos'); })
      .finally(() => setLoading(false));
    onSeleccion([]);
  }, [rut]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = docs.filter((d) => seleccion.includes(d.bill_id)).reduce((s, d) => s + d.saldo, 0);
  const toggle = (id) => onSeleccion(seleccion.includes(id) ? seleccion.filter((x) => x !== id) : [...seleccion, id]);

  return (
    <div className="col-span-2 rounded border bg-slate-50/60" data-testid="documentos-pendientes">
      <div className="flex items-center justify-between px-2 py-1.5 border-b text-xs font-medium">
        <span>Documentos pendientes del proveedor {loading && <Loader2 className="inline w-3 h-3 animate-spin ml-1" />}</span>
        <span className="tabular-nums" data-testid="documentos-total">Monto a pagar: <b>{fmtCLP(total)}</b> ({seleccion.length} doc.)</span>
      </div>
      {!rut && <p className="p-2 text-[11px] text-muted-foreground">Selecciona primero el proveedor.</p>}
      {rut && !loading && docs.length === 0 && <p className="p-2 text-[11px] text-amber-700" data-testid="documentos-vacio">El proveedor no tiene documentos pendientes en Alegra.</p>}
      {docs.length > 0 && (
        <div className="max-h-44 overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-100 text-left"><tr><th className="px-2 py-1 w-6" /><th className="px-2 py-1">N°</th><th className="px-2 py-1">Fecha</th><th className="px-2 py-1">Vence</th><th className="px-2 py-1">OC</th><th className="px-2 py-1 text-right">Total</th><th className="px-2 py-1 text-right">Saldo</th></tr></thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.bill_id} className={`border-t cursor-pointer hover:bg-indigo-50/50 ${seleccion.includes(d.bill_id) ? 'bg-indigo-50' : ''}`} onClick={() => toggle(d.bill_id)} data-testid={`doc-row-${d.bill_id}`}>
                  <td className="px-2 py-1"><input type="checkbox" readOnly checked={seleccion.includes(d.bill_id)} data-testid={`doc-check-${d.bill_id}`} /></td>
                  <td className="px-2 py-1 font-mono">{d.numero}</td>
                  <td className="px-2 py-1">{fmtFecha(d.fecha)}</td>
                  <td className="px-2 py-1">{fmtFecha(d.fecha_vencimiento)}</td>
                  <td className="px-2 py-1">{d.oc || '—'}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{fmtCLP(d.total)}</td>
                  <td className="px-2 py-1 text-right tabular-nums font-semibold">{fmtCLP(d.saldo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
